import React from 'react';
import './LoadingSpinner.css';

type SpinnerSize = 'sm' | 'md' | 'lg';
type SpinnerColor = 'primary' | 'accent' | 'white';

interface SpinnerProps {
  size?: SpinnerSize;
  color?: SpinnerColor;
  className?: string;
  style?: React.CSSProperties;
}

function Spinner({ size = 'md', color = 'primary', className = '', style }: SpinnerProps) {
  const classes = [
    'ui-spinner',
    `ui-spinner--${size}`,
    color !== 'primary' ? `ui-spinner--${color}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      className={classes}
      style={style}
      role="status"
      aria-label="Loading"
    />
  );
}

/* ── Loading overlay (centered in parent) ──────────────────────────────── */

interface LoadingOverlayProps {
  label?: string;
  size?: SpinnerSize;
}

function LoadingOverlay({ label = 'Loading…', size = 'lg' }: LoadingOverlayProps) {
  return (
    <div className="ui-loading-overlay" role="status" aria-live="polite">
      <Spinner size={size} />
      <span>{label}</span>
    </div>
  );
}

/* ── Skeleton block ────────────────────────────────────────────────────── */

interface SkeletonProps {
  variant?: 'text' | 'title' | 'circle' | 'card' | 'custom';
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

function Skeleton({
  variant = 'text',
  width,
  height,
  className = '',
  style,
}: SkeletonProps) {
  const classes = [
    'ui-skeleton',
    variant !== 'custom' ? `ui-skeleton--${variant}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const inlineStyle: React.CSSProperties = {
    ...(width !== undefined ? { width } : {}),
    ...(height !== undefined ? { height } : {}),
    ...style,
  };

  return <div className={classes} style={inlineStyle} aria-hidden="true" />;
}

export { Spinner, LoadingOverlay, Skeleton };
